import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { DateField } from '@/components/form/fields/date/DateInput';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { YesNoField } from '@/components/form/fields/yesNo/YesNoInput';
import { Counter } from '@/components/form/fields/number/Counter';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { useCompany } from '@/services/api/hooks/companies';
import { useSubmitLeaverRequest } from '@/services/api/hooks/management';
import { useGetRateCards } from '@/services/api/hooks/catalog';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { useCreateServiceRequest } from '@/services/api/hooks/serviceRequests';
import { useUser } from '@/services/authentication/hooks/user';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { Multistep } from '@/components/multistep/Multistep';
import { Step } from '@/components/multistep/Questionnaire';
import { useMultistep } from '@/components/multistep/useMultistep';
import clsx from 'clsx';
const WizardProgress: FC = () => {
  const { steps, current } = useMultistep(state => state);
  const stepKeys = Object.keys(steps);
  if (stepKeys.length === 0 || !current) return null;
  const currentIndex = stepKeys.indexOf(current);

  return (
    <div className="flex items-center justify-between mb-8 relative w-full px-4 mt-6">
      <div className="absolute top-1/2 left-4 right-4 h-1 bg-gray-200 -z-10 -translate-y-1/2" />
      <div className="absolute top-1/2 left-4 h-1 bg-primary-500 -z-10 -translate-y-1/2 transition-all duration-300" style={{ width: `${stepKeys.length > 1 ? (currentIndex / (stepKeys.length - 1)) * 100 : 100}%` }} />
      {stepKeys.map((key, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        return (
          <div key={key} className="flex flex-col items-center px-2 z-10" >
            <div className={clsx("w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm bg-white", 
              isCompleted ? "bg-primary-500 text-white" : 
              isCurrent ? "border-2 border-primary-500 text-primary-500" : 
              "border-2 border-gray-300 text-gray-400"
            )}>
              {index + 1}
            </div>
            <span className={clsx("text-xs mt-1 font-medium bg-white px-1", isCurrent ? "text-primary-600" : "text-gray-500")}>
              {key}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const UserDetailsSection: FC = () => (
  <div className="space-y-4">
    <TextField id="customerName" label="Customer Name" readonly />
    <TextField id="fullName" label="User's Name" placeholder="this is the person leaving the organisation" />
    <TextField id="workEmail" label="User's 'corporate' email" placeholder="this identifies them in our system" />
    <DateField
      id="targetDate"
      label="Termination Date"
      excludeDates={[new Date().toDateString()]}
      excludePastDates={true}
      placeholder="we will reach out to the user to confirm a suitable date that works for them after this date"
    />
  </div>
);

const ContactDetailsSection: FC<{ isPrimaryContact: boolean }> = ({ isPrimaryContact }) => (
  <div className="space-y-4">
    <YesNoField id="isPrimaryContact" label="Is the user the primary contact for this service?" />
    
    {isPrimaryContact ? (
      <>
        <TextField id="personalEmail" label="User's personal email" placeholder="used to communicate with the user after termination date" />
        <TextField id="mobileNumber" label="User's personal mobile number" placeholder="used to communicate with the user after termination date" />
      </>
    ) : (
      <>
        <TextField id="contactName" label="Contact Name" />
        <TextField id="contactEmail" label="Contact Email" />
        <TextField id="contactMobile" label="Contact Mobile" />
      </>
    )}
    
    <YesNoField 
      id="canPrintLabel" 
      label={isPrimaryContact ? "Can the user print a label?" : "Can the contact print a label?"} 
    />
  </div>
);

const CollectionAddressSection: FC = () => (
  <div className="space-y-4">
    <TextField id="addressLine1" label="Address Line 1" />
    <TextField id="addressLine2" label="Address Line 2" />
    <TextField id="town" label="Town/City" />
    <TextField id="region" label="Region/State" />
    <TextField id="postCode" label="Post Code / Zip Code" />
  </div>
);

const HardwareServicesSection: FC<{
  triggerTypes: string[];
  isTriggerTypeMaxReached: (t: string) => boolean;
  methods: any;
  inferredServices: Array<{ rateCard: any, quantity: number, labels: string[] }>;
  runningTotal: number;
}> = ({ triggerTypes, isTriggerTypeMaxReached, methods, inferredServices, runningTotal }) => (
  <div className="space-y-4">
    {triggerTypes.map((t, index) => {
      const isMaxReached = isTriggerTypeMaxReached(t);

      return (
        <div key={t} className="flex justify-between items-center py-2 border-b last:border-b-0">
          <div className="flex items-center gap-3">
            <span className="font-medium">{t}</span>
            {isMaxReached && <span className="bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded">Max Reached</span>}
          </div>
          <Controller
            name={`trigger_${index}`}
            control={methods.control}
            render={({ field }) => (
              <Counter 
                {...field} 
                id={`trigger_${index}`}
                onChange={field.onChange} 
                max={isMaxReached ? field.value : undefined} 
                min={0}
              />
            )}
          />
        </div>
      );
    })}

    {inferredServices.length > 0 && (
      <div className="pt-4 space-y-3">
        <h5 className="font-semibold text-lg">Inferred Services & Details</h5>
        {inferredServices.map(service => (
          <div key={service.rateCard.id} className="p-3 bg-white border rounded">
            <p className="font-medium">{service.quantity}x {service.rateCard.name}</p>
            {service.rateCard.propertyBag.requiresSerialNumber && (
              <div className="mt-2 space-y-2 pl-4">
                {Array.from({ length: service.quantity }).map((_, i) => (
                  <TextField 
                    key={i} 
                    id={`serialNumber_${service.rateCard.id}_${i}`} 
                    label={`${service.labels[i] || 'Item'} Serial Number`} 
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    )}
    
    <div className="flex justify-between items-center pt-4 text-xl font-bold border-t">
      <span>Estimated Retrobill Total</span>
      <span>{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(runningTotal)}</span>
    </div>
  </div>
);

const AdditionalInfoSection: FC = () => (
  <div className="space-y-4">
    <TextField id="billingReference" label="Reference" placeholder="This will appear on your invoice. If no reference is given, the users name will be used" />
    <TextAreaField id="notes" label="Notes / Special Requirements" />
  </div>
);

export const OffboardUserPage: FC = () => {
  const [uxMode, setUxMode] = useState<'flat' | 'wizard'>('flat');
  const breadcrumbs: NavPage[] = [
    {
      name: 'Management',
      to: ''
    },
    {
      name: 'Offboard User',
      to: ''
    }
  ];

  const { partnerId, companyId } = useParams();

  if (!(partnerId && companyId)) throw new Error('Missing params');
  const [company] = useCompany(partnerId, companyId);
  const [rateCards] = useGetRateCards();

  const triggerTypes = useMemo(() => {
    const types = new Set<string>();
    rateCards?.items?.forEach(card => {
      if (card.propertyBag.basePackForRequestType === 'Offboard') return;
      card.propertyBag.triggerTypes?.forEach(t => types.add(t));
    });
    return Array.from(types).sort();
  }, [rateCards]);

  const schema = useMemo(() => {
    const shape: any = {
      customerName: z.string().nonempty(),
      fullName: z.string().nonempty('User Name is required'),
      workEmail: z.string().email('Valid corporate email is required').nonempty('Corporate email is required'),
      targetDate: z.date({ required_error: 'Termination date is required' }),
      
      isPrimaryContact: z.boolean(),
      personalEmail: z.string().optional(),
      mobileNumber: z.string().optional(),
      contactName: z.string().optional(),
      contactEmail: z.string().optional(),
      contactMobile: z.string().optional(),

      addressLine1: z.string().nonempty('Address Line 1 is required'),
      addressLine2: z.string().optional(),
      town: z.string().nonempty('Town/City is required'),
      region: z.string().optional(),
      postCode: z.string().nonempty('Post Code is required'),
      
      billingReference: z.string().optional(),
      canPrintLabel: z.boolean().optional(),
      notes: z.string().optional()
    };
    
    // Add counters for trigger types
    triggerTypes.forEach((t, index) => {
      shape[`trigger_${index}`] = z.number().min(0).default(0);
    });

    return z.object(shape).catchall(z.any()).superRefine((data, ctx) => {
      if (data.isPrimaryContact) {
        if (!data.personalEmail) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Personal Email is required', path: ['personalEmail'] });
        } else if (!z.string().email().safeParse(data.personalEmail).success) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be a valid email', path: ['personalEmail'] });
        }
        if (!data.mobileNumber) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Mobile Number is required', path: ['mobileNumber'] });
        }
      } else {
        if (!data.contactName) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Contact Name is required', path: ['contactName'] });
        }
        if (!data.contactEmail) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Contact Email is required', path: ['contactEmail'] });
        } else if (!z.string().email().safeParse(data.contactEmail).success) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be a valid email', path: ['contactEmail'] });
        }
        if (!data.contactMobile) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Contact Mobile is required', path: ['contactMobile'] });
        }
      }

      // Validate dynamic serial numbers
      const activeSnKeys = new Set<string>();
      rateCards?.items?.forEach(card => {
        if (card.propertyBag.basePackForRequestType === 'Offboard') return;
        if (!card.propertyBag.requiresSerialNumber) return;

        let qty = 0;
        card.propertyBag.triggerTypes?.forEach(t => {
          const typeIndex = triggerTypes.indexOf(t);
          qty += (data[`trigger_${typeIndex}`] as number) || 0;
        });

        for (let i = 0; i < qty; i++) {
          activeSnKeys.add(`serialNumber_${card.id}_${i}`);
        }
      });

      const seenSns = new Set<string>();
      activeSnKeys.forEach(key => {
        const sn = data[key];
        if (!sn || typeof sn !== 'string' || sn.trim() === '') {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Serial number is required', path: [key] });
        } else {
          const normalizedSn = sn.toLowerCase().trim();
          if (seenSns.has(normalizedSn)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Serial numbers must be unique', path: [key] });
          }
          seenSns.add(normalizedSn);
        }
      });
    });
  }, [triggerTypes, rateCards]);

  type FormFields = z.infer<typeof schema>;

  const methods = useForm<FormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      customerName: company.name,
      isPrimaryContact: true
    }
  });

  const navigate = useNavigate();
  const routePrefix = useCustomerRoutePrefix();

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const submitRequest = useSubmitLeaverRequest();
  const createServiceRequest = useCreateServiceRequest();
  const user = useUser();

  const formValues = methods.watch();

  // Calculate inferred rate cards and serial numbers
  const { inferredServices, runningTotal } = useMemo(() => {
    let total = 0;
    const services: Array<{ rateCard: any, quantity: number, labels: string[] }> = [];

    // Auto-inject Base Pack
    const basePack = rateCards?.items?.find(c => c.propertyBag.basePackForRequestType === 'Offboard');
    if (basePack) {
      services.push({ rateCard: basePack, quantity: 1, labels: [] });
      if (basePack.price?.price) {
        total += (basePack.price.price / 100);
      }
    }

    rateCards?.items?.forEach(card => {
      if (card.propertyBag.basePackForRequestType === 'Offboard') return;

      let qty = 0;
      const labels: string[] = [];
      card.propertyBag.triggerTypes?.forEach(t => {
        const typeIndex = triggerTypes.indexOf(t);
        const addedQty = (formValues[`trigger_${typeIndex}`] as number) || 0;
        qty += addedQty;
        for (let i = 0; i < addedQty; i++) {
          labels.push(t);
        }
      });

      if (qty > 0) {
        services.push({ rateCard: card, quantity: qty, labels });
        if (card.price?.price) {
          total += (card.price.price / 100) * qty;
        }
      }
    });

    return { inferredServices: services, runningTotal: total };
  }, [rateCards, formValues]);

  // Check bonded limits for a given trigger type
  const isTriggerTypeMaxReached = (t: string) => {
    // Find rate cards triggered by t
    const triggeringCards = rateCards?.items?.filter(c => c.propertyBag.triggerTypes?.includes(t));
    if (!triggeringCards || triggeringCards.length === 0) return false;

    // Check each rate card. If ANY rate card has reached its max limit, we can't increment t.
    for (const card of triggeringCards) {
      const maxQty = card.propertyBag.groupMaxQuantity ?? Infinity;
      if (maxQty === Infinity) continue;

      const group = card.propertyBag.sharedLimitGroup;
      const bondedIds = card.propertyBag.bondedItemIds;
      
      let currentSum = 0;
      const triggersToSum = new Set<string>();

      if (group && group.trim() !== '') {
        // Shared limit group: sum all trigger types from all rate cards in this group
        const groupCards = rateCards?.items?.filter(c => c.propertyBag.sharedLimitGroup === group);
        groupCards?.forEach(c => {
          c.propertyBag.triggerTypes?.forEach(type => triggersToSum.add(type));
        });
      } else if (bondedIds && bondedIds.length > 0) {
        // Bonded items group
        const bondedCards = rateCards?.items?.filter(c => bondedIds.includes(c.id) || c.id === card.id);
        bondedCards?.forEach(c => {
          c.propertyBag.triggerTypes?.forEach(type => triggersToSum.add(type));
        });
      } else {
        // Independent item: sum only its own trigger types
        card.propertyBag.triggerTypes?.forEach(type => triggersToSum.add(type));
      }

      triggersToSum.forEach(type => {
        const typeIndex = triggerTypes.indexOf(type);
        currentSum += (formValues[`trigger_${typeIndex}`] as number) || 0;
      });

      if (currentSum >= maxQty) {
        return true; // Max reached for at least one rule
      }
    }

    return false;
  };

  const validateUserDetails = useCallback(async () => await methods.trigger(['fullName', 'workEmail', 'targetDate']), [methods]);
  const validateContactDetails = useCallback(async () => await methods.trigger(['isPrimaryContact', 'personalEmail', 'mobileNumber', 'contactName', 'contactEmail', 'contactMobile', 'canPrintLabel']), [methods]);
  const validateCollectionAddress = useCallback(async () => await methods.trigger(['addressLine1', 'addressLine2', 'town', 'region', 'postCode']), [methods]);
  const validateHardware = useCallback(async () => {
    const triggerKeys = triggerTypes.map((_, i) => `trigger_${i}`);
    const snKeys: string[] = [];
    inferredServices.forEach(service => {
      if (service.rateCard.propertyBag.requiresSerialNumber) {
        for (let i = 0; i < service.quantity; i++) {
          snKeys.push(`serialNumber_${service.rateCard.id}_${i}`);
        }
      }
    });
    return await methods.trigger([...triggerKeys, ...snKeys] as any);
  }, [methods, triggerTypes, inferredServices]);

  const onSubmit = async (data: any) => {
    // Validate serial numbers
    let missingSn = false;
    let duplicateSn = false;
    const seenSns = new Set<string>();

    inferredServices.forEach(service => {
      if (service.rateCard.propertyBag.requiresSerialNumber) {
        for (let i = 0; i < service.quantity; i++) {
          const sn = data[`serialNumber_${service.rateCard.id}_${i}`];
          if (!sn) {
            missingSn = true;
          } else {
            const normalizedSn = sn.trim().toLowerCase();
            if (seenSns.has(normalizedSn)) {
              duplicateSn = true;
            }
            seenSns.add(normalizedSn);
          }
        }
      }
    });

    if (missingSn) {
      setErrorMessage('Please provide all required serial numbers.');
      return;
    }

    if (duplicateSn) {
      setErrorMessage('Duplicate serial numbers detected. Each serial number must be unique.');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    // Format payload
    const devicesToBeCollected: string[] = [];
    inferredServices.forEach(service => {
      let snString = '';
      if (service.rateCard.propertyBag.requiresSerialNumber) {
        const sns = [];
        for (let i = 0; i < service.quantity; i++) {
          sns.push(data[`serialNumber_${service.rateCard.id}_${i}`]);
        }
        snString = ` (SN: ${sns.join(', ')})`;
      }
      devicesToBeCollected.push(`${service.quantity}x ${service.rateCard.name}${snString}`);
    });

    // Address format
    const addressParts = [data.addressLine1, data.addressLine2, data.town, data.region, data.postCode];
    const collectionAddress = addressParts.filter(Boolean).join(', ');

    // Contact Logic
    const personalEmail = data.isPrimaryContact ? data.personalEmail : data.contactEmail;
    const mobileNumber = data.isPrimaryContact ? data.mobileNumber : data.contactMobile;
    
    // Notes logic
    let extraNotes = '';
    if (!data.isPrimaryContact && data.contactName) {
      extraNotes += `Alternative Contact: ${data.contactName}\n`;
    }
    if (data.billingReference) {
      extraNotes += `Billing Ref: ${data.billingReference}\n`;
    }
    const finalNotes = data.notes ? `${extraNotes}\n${data.notes}` : extraNotes.trim();

    const hardwareSelections: Record<string, number> = {};
    triggerTypes.forEach((t, index) => {
      const quantity = data[`trigger_${index}`] as number | undefined;
      if (quantity && quantity > 0) {
        hardwareSelections[t] = quantity;
      }
    });

    try {
      // 1. Log to private ledger API
      await createServiceRequest.mutateAsync({
        params: { mspId: partnerId, customerId: companyId },
        body: {
          requestedBy: user.email,
          requestType: 'OffboardUser',
          payload: {
            ...data,
            hardwareSelections,
            inferredServices,
            runningTotal
          }
        }
      });

      // 2. Submit primary request
      const result = await submitRequest.mutateAsync({
        customerName: data.customerName,
        fullName: data.fullName,
        personalEmail: personalEmail,
        workEmail: data.workEmail,
        mobileNumber: mobileNumber,
        devicesToBeCollected,
        collectionAddress: collectionAddress,
        targetDate: data.targetDate,
        canPrintLabel: data.canPrintLabel,
        notes: finalNotes || undefined
      });

      if (result.status === 'Message sent.') {
        navigate(`${routePrefix}/management/success`);
      } else {
        setErrorMessage('Unable to submit request.');
        setLoading(false);
      }
    } catch (e) {
      setErrorMessage('Unable to submit request.');
      setLoading(false);
    }
  };

  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  const handleFinish = useCallback(async () => {
    await methods.handleSubmit((data) => onSubmitRef.current(data))();
  }, [methods]);

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex flex-col gap-2 items-start">
          <h3 className="text-3xl font-bold">Offboard User</h3>
          <p className="font-medium">This is a chargeable service</p>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto p-10">
        <div className="flex items-center gap-4 mb-8 pb-4 border-b">
          <span className="font-semibold text-lg">UX Mode:</span>
          <div className="flex rounded-md shadow-sm" role="group">
            <button 
              type="button" 
              onClick={() => setUxMode('flat')}
              className={clsx(
                "px-4 py-2 text-sm font-medium border border-gray-200 rounded-l-lg hover:bg-gray-100 hover:text-primary-700",
                uxMode === 'flat' ? "bg-primary-50 text-primary-700" : "bg-white text-gray-900"
              )}
            >
              Flat Form
            </button>
            <button 
              type="button" 
              onClick={() => setUxMode('wizard')}
              className={clsx(
                "px-4 py-2 text-sm font-medium border border-gray-200 border-l-0 rounded-r-md hover:bg-gray-100 hover:text-primary-700",
                uxMode === 'wizard' ? "bg-primary-50 text-primary-700" : "bg-white text-gray-900"
              )}
            >
              Use Wizard
            </button>
          </div>
        </div>

        <Form methods={methods as any} onSubmit={onSubmit} className={clsx(uxMode === 'flat' && "space-y-5")}>
          {uxMode === 'flat' ? (
            <>
              <div className="border p-4 rounded bg-gray-50 space-y-4">
                <h4 className="text-xl font-bold">User Details</h4>
                <UserDetailsSection />
              </div>
              <div className="border p-4 rounded bg-gray-50 space-y-4">
                <h4 className="text-xl font-bold">Contact Details</h4>
                <ContactDetailsSection isPrimaryContact={formValues.isPrimaryContact} />
              </div>
              <div className="border p-4 rounded bg-gray-50 space-y-4">
                <h4 className="text-xl font-bold">Collection Address</h4>
                <CollectionAddressSection />
              </div>
              <div className="border p-4 rounded bg-gray-50 space-y-4">
                <h4 className="text-xl font-bold">Hardware & Services</h4>
                <HardwareServicesSection triggerTypes={triggerTypes} isTriggerTypeMaxReached={isTriggerTypeMaxReached} methods={methods} inferredServices={inferredServices} runningTotal={runningTotal} />
              </div>
              <div className="border p-4 rounded bg-gray-50 space-y-4">
                <h4 className="text-xl font-bold">Additional Information</h4>
                <AdditionalInfoSection />
              </div>
              {errorMessage && <p className="text-red-500">{errorMessage}</p>}
              <Button variant={ButtonVariant.Primary} type="submit" loading={loading}>
                Submit
              </Button>
            </>
          ) : (
            <Multistep onFinish={handleFinish}>
              <WizardProgress />
              <Step 
                id="User Details" 
                title="User Details"
                validate={validateUserDetails}
              >
                <UserDetailsSection />
              </Step>
              <Step 
                id="Contact Details" 
                title="Contact Details"
                validate={validateContactDetails}
              >
                <ContactDetailsSection isPrimaryContact={formValues.isPrimaryContact} />
              </Step>
              <Step 
                id="Collection Address" 
                title="Collection Address"
                validate={validateCollectionAddress}
              >
                <CollectionAddressSection />
              </Step>
              <Step 
                id="Hardware & Services" 
                title="Hardware & Services"
                validate={validateHardware}
              >
                <HardwareServicesSection triggerTypes={triggerTypes} isTriggerTypeMaxReached={isTriggerTypeMaxReached} methods={methods} inferredServices={inferredServices} runningTotal={runningTotal} />
              </Step>
              <Step id="Additional Information" title="Additional Information">
                <AdditionalInfoSection />
                {errorMessage && <p className="text-red-500 mt-4">{errorMessage}</p>}
              </Step>
            </Multistep>
          )}
        </Form>
      </div>
    </>
  );
};
